import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddIcon, SearchIcon } from '../../bombiix-icon';
import { NewInput } from '../PIM/common/NewInput';
import CmPanelTabs from './cm-panel-tabs';
import useSaveToLibrary from './use-save-to-library';
import useLibrarySearch from './use-library-search';
import type { LibraryMatch } from './use-library-search';
import type { LibraryPanelProps, LibraryPanelTab } from './library-panel.types';

/**
 * Add to Library / Pull From Library panel opened from a library icon on a field, section, sub-section or
 * table. Add saves the current configuration under a library title; Pull searches the library for the same
 * control type and hands back the picked entry's configuration.
 * @param controlType - The library control type the entry is saved and searched under.
 * @param content - The configuration Add saves.
 * @param onPull - Receives the configuration pulled from the library.
 * @returns The panel.
 */
function LibraryPanel({ controlType, content, onPull }: LibraryPanelProps) {
  const { t } = useTranslation('custom-module-system-canvas');
  const saveToLibrary = useSaveToLibrary();
  const [activeTab, setActiveTab] = useState<LibraryPanelTab>('add');
  const [libraryTitle, setLibraryTitle] = useState('');
  const [searchText, setSearchText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const { matches, isSearching, hasSearched, loadMatch } = useLibrarySearch(controlType, activeTab === 'pull' ? searchText : '');

  const tabs = [
    { key: 'add' as const, label: t('CUSTOM_MODULE.SYSTEM_CANVAS.ADD_TO_LIBRARY') },
    { key: 'pull' as const, label: t('CUSTOM_MODULE.SYSTEM_CANVAS.PULL_FROM_LIBRARY') },
  ];

  const handleAdd = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const isSaved = await saveToLibrary(libraryTitle, controlType, content);
      if (isSaved) setLibraryTitle('');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePick = async (match: LibraryMatch) => {
    if (isPulling) return;
    setIsPulling(true);
    try {
      const record = await loadMatch(match);
      const config = record?.content?.content;
      if (!config) return;
      onPull(config);
      setSearchText('');
    } finally {
      setIsPulling(false);
    }
  };

  const loadingSpinner = <span className="spinner-border spinner-border-sm cm-field-library__spinner" role="status" aria-label={t('COMMON.LOADING')} />;

  const showNoMatch = activeTab === 'pull' && hasSearched && !isSearching && searchText.trim() !== '' && matches.length === 0;

  return (
    <div className="cm-field-library">
      <CmPanelTabs tabs={tabs} activeKey={activeTab} onChange={setActiveTab} ariaLabel={t('CUSTOM_MODULE.SYSTEM_CANVAS.SAVE_TO_LIBRARY')} />
      {activeTab === 'add' ? (
        <NewInput
          className="cm-field-library__input"
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.LIBRARY_TITLE')}
          value={libraryTitle}
          onChange={(event) => setLibraryTitle(event.target.value)}
          endAdornment={
            <>
              <span className="cm-inline-library__divider" aria-hidden="true" />
              <button
                type="button"
                className="cm-inline-library__button"
                aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.ADD_TO_LIBRARY')}
                aria-busy={isSaving}
                disabled={isSaving}
                onClick={handleAdd}
              >
                {isSaving ? loadingSpinner : <AddIcon size={LIBRARY_ICON_SIZE} />}
              </button>
            </>
          }
        />
      ) : (
        <div className="cm-field-library__search">
          <NewInput
            className="cm-field-library__input"
            placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SEARCH_LIBRARY')}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            endAdornment={isSearching || isPulling ? loadingSpinner : <SearchIcon size={LIBRARY_ICON_SIZE} />}
          />
          {matches.length > 0 && (
            <div className="suggestion-dropdown">
              {matches.map((match) => (
                <button
                  type="button"
                  key={match._id}
                  className="suggestion-dropdown-item cm-field-library__match"
                  disabled={isPulling}
                  onClick={() => handlePick(match)}
                >
                  {match.title || match._id}
                </button>
              ))}
            </div>
          )}
          {showNoMatch && <div className="text-danger f-12 m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.LIBRARY_NO_MATCH')}</div>}
        </div>
      )}
    </div>
  );
}

export default LibraryPanel;

const LIBRARY_ICON_SIZE = 20;
